/**
 * E2E-only record of the hosts the add wizard asks a Bridge to identify
 * (ENG-4927 / ENG-4940).
 *
 * The contract under test is "Studio probes only the camera's own address": a
 * hardware-free run has no camera to answer, so the observable fact is the
 * request itself. It is recorded at the one place every add path sends it,
 * `identifyHost` (device/discovery/commands/identify-host.ts), through a
 * globalThis hook so the command module needs no import of this file. Observation
 * only: scalars are copied, nothing is returned to the caller, and a throw here
 * can never fail the command.
 *
 * Split from the installer (identify-host-probe.ts) so the log is unit-testable
 * without that module's side effect — same split as
 * native-preset-submission-recorder.ts.
 */

/** One identify request, as the UI handed it to the Bridge. */
export type IdentifyHostRequest = {
  /** The Bridge asked to probe. */
  agentId: string;
  /** The single host it was asked to probe. */
  host: string;
};

/** Bounded so a long session cannot grow the log without limit. */
const MAX_REQUESTS = 256;
const requests: IdentifyHostRequest[] = [];

/** What the probe exposes to Playwright and to `identifyHost`'s hook. */
export type IdentifyHostProbe = {
  /** Record one request. Called by `identifyHost` before the command is sent. */
  record(agentId: string, host: string): void;
  /** Requests recorded since the last `reset`, oldest first. */
  requests(): IdentifyHostRequest[];
  /** Forget earlier requests, so a test reads only what its own clicks sent. */
  reset(): void;
};

/** Record one identify request. Never throws. */
export const recordIdentifyHostRequest = (agentId: string, host: string): void => {
  try {
    if (requests.length >= MAX_REQUESTS) return;
    requests.push({ agentId: String(agentId), host: String(host) });
  } catch {
    // Diagnostics must not affect the request being diagnosed.
  }
};

/** Copy of the log, so a caller can never mutate it. */
export const identifyHostRequests = (): IdentifyHostRequest[] => requests.map((r) => ({ ...r }));

/** Empty the log. */
export const resetIdentifyHostRequests = (): void => {
  requests.length = 0;
};
